import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Search, CreditCard, DollarSign, User, 
  Calendar, CheckCircle, XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`, { withCredentials: true });
      setPayments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch payments error:', error);
      toast.error('Ödemeler yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = [...payments];

    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.payment_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.booking_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.facility_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPayments(filtered);
  };

  const getStatusBadge = (status) => {
    const config = {
      PAID: { label: 'Ödendi', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      PENDING: { label: 'Bekliyor', color: 'bg-orange-100 text-orange-700', icon: CreditCard },
      CANCELLED: { label: 'İptal', color: 'bg-red-100 text-red-700', icon: XCircle },
      MOCK: { label: 'Test', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
      NONE: { label: 'Yok', color: 'bg-slate-100 text-slate-700', icon: XCircle }
    };
    const c = config[status] || config.NONE;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidPayments = payments.filter(p => p.status === 'PAID');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ödemeler</h1>
          <p className="text-sm text-slate-600 mt-1">
            Toplam {payments.length} ödeme • {paidPayments.length} Başarılı • Toplam Tutar: {totalAmount.toLocaleString('tr-TR')} ₺
          </p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Ödeme ID, rezervasyon kodu, kullanıcı veya tesis ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ödeme ID</TableHead>
                <TableHead>Rezervasyon Kodu</TableHead>
                <TableHead>Tesis</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead>Durum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    Ödeme bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.payment_id}>
                    <TableCell className="font-mono text-xs">
                      {payment.payment_id}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {payment.booking_code || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {payment.facility_name || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <User className="w-3 h-3 text-slate-400" />
                        {payment.user_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.booking_date || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 font-medium">
                        <DollarSign className="w-3 h-3" />
                        {payment.amount.toLocaleString('tr-TR')} ₺
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {payment.provider === 'bank_transfer' ? 'Havale' : payment.provider}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(payment.status)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsPage;
